import type { SupabaseClient } from "@supabase/supabase-js";

// Route editor data layer (CONSOLE-SETUP-ROUTE-EDITOR). Reads: member-readable sites/routes
// tables through pinned column allowlists. Writes: ONLY the hosted route CRUD RPCs
// (e2f2637) - schedule.manage gated server-side, archive-over-delete so history survives.
// Checkpoint management is a separate hosted contract and deliberately absent here.

export interface SiteRow {
  siteId: string;
  siteName: string;
}

export interface EditableRoute {
  routeId: string;
  siteId: string;
  name: string;
  description: string | null;
  active: boolean;
}

export const SITE_COLUMNS = "id, name";
export const ROUTE_COLUMNS = "id, site_id, name, description, active";

const LIST_LIMIT = 200;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** Sites for the route picker (members can read sites). Throws safe. */
export async function fetchSites(client: SupabaseClient, accountId: string): Promise<SiteRow[]> {
  const { data, error } = await client
    .from("sites")
    .select(SITE_COLUMNS)
    .eq("account_id", accountId)
    .order("name", { ascending: true })
    .limit(LIST_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load sites.");
  }
  const sites: SiteRow[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const siteId = asString(raw.id);
    if (siteId === null) continue;
    sites.push({ siteId, siteName: asString(raw.name) ?? "Site" });
  }
  return sites;
}

/** All routes (active + archived) for the editor list. Throws safe. */
export async function fetchEditableRoutes(
  client: SupabaseClient,
  accountId: string,
): Promise<EditableRoute[]> {
  const { data, error } = await client
    .from("routes")
    .select(ROUTE_COLUMNS)
    .eq("account_id", accountId)
    .order("name", { ascending: true })
    .limit(LIST_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load routes.");
  }
  const routes: EditableRoute[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const routeId = asString(raw.id);
    const siteId = asString(raw.site_id);
    if (routeId === null || siteId === null) continue;
    routes.push({
      routeId,
      siteId,
      name: asString(raw.name) ?? "Route",
      description: asString(raw.description),
      active: raw.active === true,
    });
  }
  return routes;
}

export interface ScheduleRouteOption {
  routeId: string;
  routeName: string;
  siteId: string;
  siteName: string;
}

/** Active routes with site labels for schedule template pickers (not rollout-gated). */
export async function fetchScheduleRouteOptions(
  client: SupabaseClient,
  accountId: string,
): Promise<ScheduleRouteOption[]> {
  const [sites, routes] = await Promise.all([
    fetchSites(client, accountId),
    fetchEditableRoutes(client, accountId),
  ]);
  const siteNameById = new Map(sites.map((site) => [site.siteId, site.siteName]));
  return routes
    .filter((route) => route.active)
    .map((route) => ({
      routeId: route.routeId,
      routeName: route.name,
      siteId: route.siteId,
      siteName: siteNameById.get(route.siteId) ?? "Site",
    }));
}

export const SAFE_ROUTE_WRITE_FAILURE = "Couldn't save that route change - please try again.";

// Contract-stable, value-free sentences from the hosted route CRUD RPCs (e2f2637).
const KNOWN_ROUTE_WRITE_FAILURES: ReadonlySet<string> = new Set([
  "Caller is not authorized to manage schedules for this account.",
  "Route name is required.",
  "Site was not found for this account.",
  "Route was not found for this account.",
  "Archived routes cannot be updated.",
  "Site id is required to create routes.",
  "Site id is required to update routes.",
]);

export type RouteWriteOutcome = { ok: true; routeId: string } | { ok: false; reason: string };

async function runRouteWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<RouteWriteOutcome> {
  try {
    const { data, error } = await client.rpc(fn, params);
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_ROUTE_WRITE_FAILURES.has(message) ? message : SAFE_ROUTE_WRITE_FAILURE,
      };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (row === undefined || typeof row.route_id !== "string") {
      return { ok: false, reason: SAFE_ROUTE_WRITE_FAILURE };
    }
    return { ok: true, routeId: row.route_id };
  } catch {
    return { ok: false, reason: SAFE_ROUTE_WRITE_FAILURE };
  }
}

/** Create a route on a site. Never throws. */
export async function createRoute(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  name: string,
  description: string | null,
): Promise<RouteWriteOutcome> {
  return runRouteWrite(client, "create_route", {
    p_account_id: accountId,
    p_site_id: siteId,
    p_name: name,
    p_description: description,
  });
}

/** Update an ACTIVE route (archived ones are refused server-side). Never throws. */
export async function updateRoute(
  client: SupabaseClient,
  accountId: string,
  routeId: string,
  siteId: string,
  name: string,
  description: string | null,
): Promise<RouteWriteOutcome> {
  return runRouteWrite(client, "update_route", {
    p_account_id: accountId,
    p_route_id: routeId,
    p_site_id: siteId,
    p_name: name,
    p_description: description,
  });
}

/** Archive a route (history survives; it leaves pickers). Never throws. */
export async function archiveRoute(
  client: SupabaseClient,
  accountId: string,
  routeId: string,
): Promise<RouteWriteOutcome> {
  return runRouteWrite(client, "archive_route", { p_account_id: accountId, p_route_id: routeId });
}
